import {remCalc} from '../../../plugins/px-to-rem-babel-custom-plugin/utils/remCalc'

/**
 * Screen-reader-only styles matching the accessibility consultant's specification.
 *
 * Use with `<Box as="span" sx={srOnlyStyles}>` to create text that is
 * visually hidden but announced by assistive technologies.
 *
 * Extends the standard visually-hidden pattern with defensive properties
 * (display, color, background-color, visibility) to prevent inherited styles
 * from inadvertently hiding the content from screen readers.
 */
export const srOnlyStyles = {
    position: 'absolute',
    width: remCalc(1),
    height: remCalc(1),
    padding: 'spacing.none',
    margin: `-${remCalc(1)}`,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    border: '0',
    display: 'block !important',
    color: '#262626 !important',
    backgroundColor: '#ffffff !important',
    visibility: 'visible',
    whiteSpace: 'nowrap',
} as const

/**
 * WCAG 1.4.5 – Live text styles for images containing burned-in text.
 *
 * Use with `<Box as="span" className="cc-livetext" sx={liveTextStyles}>` to
 * render selectable, zoomable text that is visually hidden by default but
 * accessible to assistive technologies.
 *
 * When `html.un-assistive` is active, the text becomes visible below the image
 * (the visible state is handled by a global CSS rule in globalStyles.ts).
 *
 * Unlike srOnlyStyles, this pattern keeps the text in DOM flow for browser
 * selection and zoom, satisfying WCAG 1.4.5 requirements.
 */
export const liveTextStyles = {
    position: 'absolute',
    width: remCalc(1),
    height: remCalc(1),
    padding: '0',
    margin: `-${remCalc(1)}`,
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap',
    border: '0',
} as const

/**
 * Visible state for liveTextStyles, applied when `html.un-assistive` is active.
 * Used in globalStyles.ts as `.un-assistive .cc-livetext` selector.
 *
 * Uses position:relative + z-index to ensure visibility above absolutely-positioned
 * images/overlays within editorial module containers.
 */
export const liveTextVisibleStyles = {
    position: 'relative',
    width: '100%',
    height: 'auto',
    padding: `${remCalc(8)} ${remCalc(16)}`,
    margin: '0',
    overflow: 'visible',
    clipPath: 'none',
    whiteSpace: 'normal',
    display: 'block',
    fontSize: remCalc(14),
    lineHeight: 1.5,
    color: '#262626',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    textAlign: 'center' as const,
    zIndex: 10,
} as const

export {remCalc}
